/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable complexity */

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import party from "party-js";
import { useEffect, type FC, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsFillExclamationTriangleFill } from "react-icons/bs";
import { FiExternalLink } from "react-icons/fi";
import { IoRefresh } from "react-icons/io5";

import Images from "styles/image-library";
import { Button } from "tw/components/atoms/Button";
import { Markdown } from "tw/components/atoms/Markdown";
import { format2Dec } from "utils/number-to-short-version";
import { shortenAddress } from "utils/shorten-address";

export enum TransactionType {
  ClaimMndeIncentives,
  LiquidStake,
  MarinadeNativeStake,
  Unstake,
  DirectVoteMarinade,
  DirectVoteValidator,
  RemoveDirectVoteMarinade,
  RemoveDirectVoteValidator,
  StakeMnde,
  UnstakeMnde,
}

const NonVoteTransactions = [
  TransactionType.ClaimMndeIncentives,
  TransactionType.LiquidStake,
  TransactionType.MarinadeNativeStake,
  TransactionType.Unstake,
  TransactionType.StakeMnde,
  TransactionType.UnstakeMnde,
];

enum TransactionState {
  WalletAction,
  Processing,
  Succeeded,
  Failed,
}

const InfoItem = ({
  label,
  value,
  link,
}: {
  label: string;
  value: string;
  link?: string;
}) => {
  let child: ReactNode;
  if (link) {
    child = (
      <Link
        href={link}
        target="_blank"
        rel="noreferrer noopener"
        className="flex text-base gap-2 items-center text-primary hover:underline"
      >
        {value}
        <FiExternalLink />
      </Link>
    );
  } else {
    child = (
      <p className="flex gap-2 text-base items-center text-black">{value}</p>
    );
  }

  return (
    <div className="font-semibold text-sm text-secondary-text">
      {label}
      {child}
    </div>
  );
};

export interface ProcessTransactionBodyProps {
  txType: TransactionType;
  signature?: string;
  success?: boolean;
  error?: string;
  amount?: number;
  amountToReceive?: number;
  apy?: string | number;
  delegation?: string;
  delegatedTo?: string;
  timeToReceive?: string;
  icon?: string;
  additionalNote?: string;
  redirectUrl?: string;
  closeSettingsPopup: () => void;
  tryAgainCallback: () => void;
}

export const ProcessTransactionBody: FC<ProcessTransactionBodyProps> = ({
  txType,
  signature,
  success,
  error,
  amount,
  amountToReceive,
  apy,
  redirectUrl,
  delegation,
  delegatedTo,
  timeToReceive,
  icon,
  additionalNote,
  closeSettingsPopup,
  tryAgainCallback,
}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const [showHighlightBg, setShowHighlightBg] = useState(false);

  const Configs: Record<
    TransactionType,
    {
      icon: string;
      title: string;
      successTitle: string;
      failureTitle: string;
      symbol: string;
      receiveSymbol?: string;
    }
  > = {
    0: {
      icon: Images.ilustrations_mnde_reward,
      title: t("appPage.transaction-modals.claiming-title"),
      successTitle: t("appPage.transaction-modals.claiming-title-success"),
      failureTitle: t("appPage.transaction-modals.claiming-title-failure"),
      symbol: "MNDE",
      receiveSymbol: "locked MNDE",
    },
    1: {
      icon: Images.icons_mSOL,
      title: t("appPage.transaction-modals.staking-title"),
      successTitle: t("appPage.transaction-modals.staking-title-success"),
      failureTitle: t(
        "appPage.transaction-modals.liquid-staking-title-failure"
      ),
      symbol: "SOL",
      receiveSymbol: "mSOL",
    },
    2: {
      icon: Images.icons_marinade_black,
      title: t("appPage.transaction-modals.staking-title"),
      successTitle: t("appPage.transaction-modals.staking-title-success"),
      failureTitle: t(
        "appPage.transaction-modals.native-staking-title-failure"
      ),
      symbol: "SOL",
    },
    3: {
      icon: Images.icons_solana_dark,
      title: t("appPage.transaction-modals.unstaking-title"),
      successTitle: t("appPage.transaction-modals.unstaking-title-success"),
      failureTitle: t("appPage.transaction-modals.unstaking-title-failure"),
      symbol: "SOL",
    },
    4: {
      icon: Images.icons_directed_to_marinade,
      title: t("appPage.transaction-modals.you-are-now-directing"),
      successTitle: t("appPage.transaction-modals.succesfully-directed"),
      failureTitle: t("appPage.transaction-modals.failed-directed"),
      symbol: "SOL",
    },
    5: {
      icon: Images.icons_directed_to_validator,
      title: t("appPage.transaction-modals.you-are-now-directing"),
      successTitle: t("appPage.transaction-modals.succesfully-directed"),
      failureTitle: t("appPage.transaction-modals.failed-directed"),
      symbol: "SOL",
    },
    6: {
      icon: Images.icons_directed_to_marinade,
      title: t("appPage.transaction-modals.remove-directing"),
      successTitle: t(
        "appPage.transaction-modals.succesfully-removed-directed"
      ),
      failureTitle: t("appPage.transaction-modals.failed-remove-directed"),
      symbol: "SOL",
    },
    7: {
      icon: Images.icons_directed_to_validator,
      title: t("appPage.transaction-modals.remove-directing"),
      successTitle: t(
        "appPage.transaction-modals.succesfully-removed-directed"
      ),
      failureTitle: t("appPage.transaction-modals.failed-remove-directed"),
      symbol: "SOL",
    },
    8: {
      icon: Images.icons_mnde,
      title: t("appPage.transaction-modals.staking-title"),
      successTitle: t("appPage.transaction-modals.staking-title-success"),
      failureTitle: t(
        "appPage.transaction-modals.liquid-staking-title-failure"
      ),
      symbol: "MNDE",
    },
    9: {
      icon: Images.icons_mnde,
      title: t("appPage.transaction-modals.unstaking-title"),
      successTitle: t("appPage.transaction-modals.unstaking-title-success"),
      failureTitle: t("appPage.transaction-modals.unstaking-title-failure"),
      symbol: "MNDE",
    },
  };

  const getState = () => {
    if (success === true) {
      return TransactionState.Succeeded;
    }
    if (success === false) {
      return TransactionState.Failed;
    }
    if (signature) {
      return TransactionState.Processing;
    }
    return TransactionState.WalletAction;
  };

  const txState = getState();
  const txFinalized =
    txState === TransactionState.Succeeded ||
    txState === TransactionState.Failed;
  const config = Configs[txType];

  const getTitle = () => {
    if (!txFinalized) {
      return config.title;
    }

    return txState === TransactionState.Succeeded
      ? config.successTitle
      : config.failureTitle;
  };

  useEffect(() => {
    if (txState === TransactionState.Succeeded) {
      setShowHighlightBg(true);
      const imageElement = document.querySelector(
        ".confetti-target"
      ) as HTMLElement;
      if (imageElement) {
        party.confetti(imageElement, { count: 70 });
      }
    }
  }, [txState]);

  /**
   * Pick font size based on amount of characters in formatted value
   * to make sure we fit into available space and do not cut the value
   * Return tailwind class
   */
  const getValueFontSizeClass = (formattedValue: string) => {
    const stringLength = formattedValue ? formattedValue.toString().length : 0;
    if (stringLength <= 6) {
      return "text-56";
    }
    if (stringLength <= 8) {
      return "text-48";
    }

    return "text-40";
  };

  return (
    <div className="w-full flex flex-col items-center md:pb-4 px-2 md:px-0">
      <div className="relative mt-4">
        {showHighlightBg && (
          <div
            className={`absolute h-[391px] w-[411px] left-0 top-0 -translate-y-[45%] -translate-x-[45%] z-0 transition-opacity duration-500 ${
              showHighlightBg ? "opacity-100" : "opacity-0"
            }`}
          >
            <Image
              src={Images.ilustrations_highlight_background}
              alt="Highlight background"
              fill
            />
          </div>
        )}
        <div className="relative z-10">
          <Image
            src={icon ?? config.icon}
            height={64}
            width={64}
            alt="Input logo"
            className={`confetti-target ${
              NonVoteTransactions.includes(txType) ? "rounded-full" : ""
            }`}
          />
        </div>
      </div>
      <span className="text-center mt-8 text-2xl font-semibold">
        {getTitle()}
      </span>
      {error && (
        <div className="flex gap-2 items-center pl-2 pr-3 py-0.5 text-error-text bg-error-text/10 rounded-lg mt-2">
          <BsFillExclamationTriangleFill className="text-alert-text h-5 w-5" />
          {error}
        </div>
      )}
      <div className="relative mt-4">
        <p
          className={`w-full text-center font-bold ${getValueFontSizeClass(
            format2Dec(amount ?? 0)
          )}`}
        >
          {format2Dec(amount ?? 0)}
        </p>
        <span className="absolute top-0 right-0 translate-x-full text-tertiary-text text-xl">
          {config.symbol}
        </span>
      </div>
      {apy !== undefined && typeof apy === "number" && apy > 0 ? (
        <div className="bg-primary/10 text-primary text-base font-semibold px-2.5 py-0.5 rounded-full mt-1">
          {t("appPage.transaction-modals.apy")} {format2Dec(apy)}%
        </div>
      ) : undefined}
      {apy !== undefined && typeof apy === "string" ? (
        <div className="bg-primary/10 text-primary text-base font-semibold px-2.5 py-0.5 rounded-full mt-1">
          {apy}
        </div>
      ) : undefined}
      <div className="w-full mt-4 grid grid-cols-2 gap-y-2 gap-x-20">
        {timeToReceive && (
          <InfoItem
            label={t("appPage.transaction-modals.receive-time")}
            value={timeToReceive}
          />
        )}
        {delegatedTo && (
          <InfoItem
            label={t("appPage.transaction-modals.directed-to")}
            value={delegatedTo}
          />
        )}
        {delegation && (
          <InfoItem
            label={t("appPage.transaction-modals.rebalancing")}
            value={delegation}
          />
        )}
        {amountToReceive !== undefined && (
          <InfoItem
            label={t("appPage.transaction-modals.receive")}
            value={`${format2Dec(amountToReceive)} ${
              config.receiveSymbol ?? config.symbol
            }`}
          />
        )}
        {signature && (
          <InfoItem
            label={t("appPage.transaction-modals.tx")}
            value={shortenAddress(signature)}
            link={`https://solscan.io/tx/${signature}`}
          />
        )}
      </div>
      {additionalNote && <Markdown className="mt-4" text={additionalNote} />}
      {txFinalized ? (
        <div className="w-full">
          {txState === TransactionState.Failed ? (
            <Button
              variant="primary"
              label={t("appPage.transaction-modals.retry")}
              leftIcon={<IoRefresh className="w-6 h-6" />}
              className="w-full mb-2 mt-8"
              onClick={tryAgainCallback}
            />
          ) : undefined}
          {NonVoteTransactions.includes(txType) ? (
            <Button
              variant="secondary"
              label={
                txState === TransactionState.Succeeded
                  ? t("appPage.transaction-modals.back-button")
                  : t("appPage.transaction-modals.cancel-button")
              }
              className={`w-full ${
                txState === TransactionState.Succeeded ? "mt-8" : ""
              }`}
              onClick={() => {
                if (txState === TransactionState.Succeeded) {
                  if (redirectUrl) {
                    router.push(redirectUrl);
                  } else {
                    router.push("/app");
                  }
                }
                closeSettingsPopup();
              }}
            />
          ) : undefined}
          {!NonVoteTransactions.includes(txType) ? (
            <Button
              variant="secondary"
              label={
                txState === TransactionState.Succeeded
                  ? t("appPage.transaction-modals.go-back-button")
                  : t("appPage.transaction-modals.cancel-button")
              }
              className={`w-full ${
                txState === TransactionState.Succeeded ? "mt-8" : ""
              }`}
              onClick={() => {
                closeSettingsPopup();
              }}
            />
          ) : undefined}
        </div>
      ) : (
        <Button
          variant="secondary"
          label={
            txState === TransactionState.Processing
              ? t("appPage.transaction-modals.processing")
              : t("appPage.transaction-modals.wallet-promt")
          }
          className="w-full mt-8"
          isLoading
          disabled
        />
      )}
    </div>
  );
};
